(() => {
  const STORAGE_KEY = 'mhcStoreModeListV1';

  document.documentElement.classList.add('mhc-store-mode-active');

  function debounce(callback, wait) {
    let timeout;
    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => callback(...args), wait);
    };
  }

  function initialize(root) {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const form = root.querySelector('[data-mhc-store-search-form]');
    const input = root.querySelector('[data-mhc-store-search-input]');
    const results = root.querySelector('[data-mhc-store-results]');
    const status = root.querySelector('[data-mhc-store-status]');
    const listContainer = root.querySelector('[data-mhc-store-list]');
    const count = root.querySelector('[data-mhc-store-count]');
    const actions = root.querySelector('[data-mhc-store-list-actions]');
    const clearCompleted = root.querySelector('[data-mhc-store-clear-completed]');
    const printButton = root.querySelector('[data-mhc-store-print]');
    let activeRequest;
    let items = loadItems();

    function loadItems() {
      try {
        const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
        return Array.isArray(stored) ? stored : [];
      } catch (error) {
        return [];
      }
    }

    function saveItems() {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch (error) {
        return;
      }
    }

    function announce(message) {
      status.textContent = message;
    }

    function itemCountText(total) {
      const template = total === 1 ? root.dataset.itemCountOne : root.dataset.itemCountOther;
      return template.replace('{{ count }}', String(total));
    }

    function renderList() {
      listContainer.replaceChildren();
      count.textContent = String(items.length);
      count.setAttribute('aria-label', itemCountText(items.length));
      actions.hidden = items.length === 0;

      if (items.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'mhc-store-mode__list-empty';
        empty.textContent = root.dataset.emptyListText;
        listContainer.appendChild(empty);
        syncResultButtons();
        return;
      }

      items
        .slice()
        .sort((a, b) => a.location.localeCompare(b.location, undefined, { numeric: true }))
        .forEach((item) => {
          const row = document.createElement('div');
          row.className = 'mhc-store-list-item';
          row.dataset.productId = item.id;
          row.dataset.completed = String(Boolean(item.completed));

          const checkbox = document.createElement('input');
          checkbox.className = 'mhc-store-list-item__check';
          checkbox.type = 'checkbox';
          checkbox.checked = Boolean(item.completed);
          checkbox.setAttribute('aria-label', item.title);

          const copy = document.createElement('div');
          const link = document.createElement('a');
          link.className = 'mhc-store-list-item__title';
          link.href = item.url;
          link.textContent = item.title;
          const meta = document.createElement('span');
          meta.className = 'mhc-store-list-item__meta';
          meta.textContent = [item.location, item.sku].filter(Boolean).join(' · ');
          copy.append(link, meta);

          const remove = document.createElement('button');
          remove.className = 'mhc-store-list-item__remove';
          remove.type = 'button';
          remove.dataset.removeProduct = item.id;
          remove.setAttribute('aria-label', `${root.dataset.removeText}: ${item.title}`);
          remove.innerHTML = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 7h14M9 7V4h6v3m-8 0 1 13h8l1-13M10 11v5m4-5v5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

          checkbox.addEventListener('change', () => {
            const target = items.find((entry) => entry.id === item.id);
            if (!target) return;
            target.completed = checkbox.checked;
            saveItems();
            row.dataset.completed = String(checkbox.checked);
          });

          row.append(checkbox, copy, remove);
          listContainer.appendChild(row);
        });

      syncResultButtons();
    }

    function syncResultButtons() {
      const ids = new Set(items.map((item) => item.id));
      results.querySelectorAll('[data-mhc-add-product]').forEach((button) => {
        const added = ids.has(button.dataset.productId);
        button.disabled = added;
        button.textContent = added ? root.dataset.addedText : root.dataset.addText;
      });
    }

    function addProduct(button) {
      const id = button.dataset.productId;
      if (!id || items.some((item) => item.id === id)) return;

      items.push({
        id,
        title: button.dataset.productTitle || '',
        url: button.dataset.productUrl || '',
        image: button.dataset.productImage || '',
        price: button.dataset.productPrice || '',
        location: button.dataset.productLocation || '',
        sku: button.dataset.productSku || '',
        completed: false
      });
      saveItems();
      renderList();
      announce(`${button.dataset.productTitle} — ${root.dataset.addedText}`);
    }

    function removeProduct(id) {
      items = items.filter((item) => item.id !== id);
      saveItems();
      renderList();
    }

    async function search(query) {
      const term = query.trim();
      if (term.length < 2) return;

      if (activeRequest) activeRequest.abort();
      activeRequest = new AbortController();

      results.innerHTML = '<div class="mhc-store-mode__loading" aria-hidden="true"><span class="mhc-store-mode__spinner"></span></div>';
      input.setAttribute('aria-expanded', 'true');
      announce(root.dataset.loadingText);

      const params = new URLSearchParams();
      params.set('q', term);
      params.set('resources[type]', 'product');
      params.set('resources[limit]', root.dataset.searchLimit || '8');
      params.set('resources[options][fields]', 'title,product_type,variants.title,vendor,variants.sku,tag');
      params.set('resources[options][unavailable_products]', 'last');
      params.set('section_id', root.dataset.sectionId);

      try {
        const response = await fetch(`${root.dataset.searchUrl}?${params.toString()}`, {
          signal: activeRequest.signal,
          headers: { Accept: 'text/html' }
        });
        if (!response.ok) throw new Error(String(response.status));

        const template = document.createElement('template');
        template.innerHTML = await response.text();
        const section = template.content.querySelector(`#shopify-section-${root.dataset.sectionId}`);
        results.innerHTML = section ? section.innerHTML : '';
        syncResultButtons();
        announce(root.dataset.resultsUpdatedText);
      } catch (error) {
        if (error.name === 'AbortError') return;
        const message = document.createElement('div');
        const copy = document.createElement('p');
        message.className = 'mhc-store-mode__message';
        copy.textContent = root.dataset.errorText;
        message.appendChild(copy);
        results.replaceChildren(message);
        announce(root.dataset.errorText);
      }
    }

    const debouncedSearch = debounce(() => search(input.value), 300);

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      search(input.value);
    });

    input.addEventListener('input', () => {
      if (input.value.trim().length >= 2) debouncedSearch();
    });

    results.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-mhc-quick-view-open]');
      if (opener) {
        const dialog = document.getElementById(opener.dataset.mhcQuickViewOpen);
        if (dialog && typeof dialog.showModal === 'function') dialog.showModal();
        return;
      }

      const closer = event.target.closest('[data-mhc-quick-view-close]');
      if (closer) {
        closer.closest('dialog')?.close();
        return;
      }

      if (event.target.matches('.mhc-store-quick-view')) {
        event.target.close();
        return;
      }

      const button = event.target.closest('[data-mhc-add-product]');
      if (button) addProduct(button);
    });

    listContainer.addEventListener('click', (event) => {
      const button = event.target.closest('[data-remove-product]');
      if (button) removeProduct(button.dataset.removeProduct);
    });

    clearCompleted.addEventListener('click', () => {
      items = items.filter((item) => !item.completed);
      saveItems();
      renderList();
    });

    printButton.addEventListener('click', () => window.print());

    renderList();
  }

  function initializeAll() {
    document.querySelectorAll('[data-mhc-store-mode]').forEach(initialize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAll);
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', initializeAll);
})();
